// agenda.js — grouping and labelling for the Schedule tab's Agenda view (#43).
//
// The Agenda view is a vertical, date-grouped list of jobs: one heading per day
// ("TODAY — Tue, Jul 28", "Tomorrow — Wed, Jul 29", "Thu, Jul 30") with the
// day's jobs beneath it. It exists so a crew opening the app in portrait can
// see today's work without picking a column first.
//
// ── Why this logic lives here and not in the component ──────────────────────
//
// Every decision below is one a person can disagree with, and every one of them
// fails SILENTLY if it is wrong: a job in the wrong day group, a job dropped
// from the list entirely, or "today" landing on the wrong date west of
// Greenwich. None of those throw, none of them look broken in a screenshot, and
// all of them mean a crew drives to the wrong place or misses a job.
//
// So it is a pure module with a test file, per CLAUDE.md ("pure logic in
// modules, with scenarios in tests"), and the component below it only renders.
//
// ── Dates come from utils/scheduleDates.js ──────────────────────────────────
//
// The date primitives this file used to define — toISO, todayISO, addDaysISO,
// jobOnDay — moved there in #146, because WeekView, MonthView and CrewWeekView
// each had their own copy of the same functions. They decide which day a job
// lands on, so two copies drifting apart puts a job on Tuesday in one view and
// Wednesday in another. Read the reasoning there, including why parsing
// happens at local noon rather than midnight.
//
// ── What is deliberately NOT here ───────────────────────────────────────────
//
// A time of day. There is no clock-time column on `jobs` — only
// `scheduled_date` and `scheduled_end_date` (verified against
// server/database.js and every `ALTER TABLE jobs` in it). So the agenda's
// leading column carries the job's DURATION, which is real data, and never a
// start time, which would have to be invented. See `jobDuration` below.

import { atNoon, addDaysISO, jobOnDay } from './scheduleDates';

/**
 * Workable jobs with no date yet — the agenda's trailing "Unscheduled" group.
 *
 * Completed work is excluded on purpose. A finished job with no date has no
 * place on a list organised by when work happens; it lives in the board's
 * Completed column. Mirrors WeekView.js:52, which builds its rail the same way.
 */
export function isUnscheduled(job) {
  return !!job && !job.scheduled_date && ['todo', 'active', 'paused'].includes(job.status);
}

/**
 * Nobody is on this job.
 *
 * Both halves are required. `jobs.user_id` is the lead pointer and
 * `job_assignees` is the additive crew roster (CLAUDE.md), and the two CAN
 * disagree: PATCH /jobs/:id/schedule writes `user_id` without touching the
 * roster (server/routes/jobs.js:891), while setJobRoster mirrors the lead back
 * into `user_id` (server/utils/crew.js:32). Testing only `user_id` would call a
 * fully-crewed job unassigned whenever the roster was set by the other path.
 */
export function isUnassigned(job) {
  if (!job) return false;
  return job.user_id == null && (job.assignees || []).length === 0;
}

/** 'today' | 'tomorrow' | 'yesterday' | null, by exact string match. */
export function relativeDay(iso, today) {
  if (!iso || !today) return null;
  if (iso === today) return 'today';
  if (iso === addDaysISO(today, 1)) return 'tomorrow';
  if (iso === addDaysISO(today, -1)) return 'yesterday';
  return null;
}

const RELATIVE_LABEL = { today: 'Today', tomorrow: 'Tomorrow', yesterday: 'Yesterday' };

/**
 * The two halves of a day heading: an optional relative word, and the date.
 *
 * Both are always rendered — "Today" alone forces a crew to work out the date,
 * and "Tue, Jul 28" alone is the thing this view exists to fix.
 */
export function dayHeading(iso, today) {
  const relative = relativeDay(iso, today);
  return {
    iso,
    relative,
    relativeLabel: relative ? RELATIVE_LABEL[relative] : null,
    dateLabel: atNoon(iso).toLocaleDateString(undefined, {
      weekday: 'short', month: 'short', day: 'numeric',
    }),
  };
}

/** "1 job" / "3 jobs" — the count that sits on the right of each heading. */
export function countLabel(n) {
  return `${n} ${n === 1 ? 'job' : 'jobs'}`;
}

/**
 * Order within a day: unfinished work first, then whatever the manager's manual
 * ordering says.
 *
 * Completed jobs sinking to the bottom is the one opinion here. A crew reading
 * today's list wants what is LEFT at the top; by mid-afternoon the finished
 * jobs would otherwise be interleaved through it. `sort_order` then `id` matches
 * the ORDER BY the server already applies (server/routes/jobs.js:197), so the
 * agenda and the board never disagree about sequence.
 */
export function agendaOrder(a, b) {
  const doneA = a.status === 'completed' ? 1 : 0;
  const doneB = b.status === 'completed' ? 1 : 0;
  if (doneA !== doneB) return doneA - doneB;
  const soA = Number.isFinite(Number(a.sort_order)) ? Number(a.sort_order) : 0;
  const soB = Number.isFinite(Number(b.sort_order)) ? Number(b.sort_order) : 0;
  if (soA !== soB) return soA - soB;
  return (a.id || 0) - (b.id || 0);
}

/**
 * The state the left colour rail encodes, in priority order.
 *
 * Priority matters: a blocked job that is also in progress reads as blocked,
 * because that is the one a manager has to do something about.
 *
 * "needs-crew" is amber and NOT red, which was an explicit decision on #43: an
 * unassigned job is a scheduling gap, not a failure. Red is reserved for things
 * that are actually wrong, or the palette stops meaning anything.
 */
export function railState(job) {
  if (!job) return 'todo';
  if (job.blocked_reason && job.status !== 'completed') return 'blocked';
  if (job.status === 'completed') return 'done';
  if (job.status === 'active') return 'live';
  if (job.status === 'paused') return 'paused';
  if (isUnassigned(job)) return 'needs-crew';
  return 'todo';
}

/** Seconds -> "2h", "45m", "1h 30m". Zero/absent is null, so callers can omit it. */
export function compactDuration(sec) {
  const n = Number(sec);
  if (!Number.isFinite(n) || n < 60) return null;
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}

/**
 * What the agenda row's leading column shows.
 *
 * This is the honest answer to "time-led rows" given the schema: there is no
 * start time to lead with, so the lead is how LONG the job is — the estimate
 * when one exists, otherwise the time logged so far. `logged` is returned
 * alongside for the secondary line, and `overBudget` drives the warning colour,
 * matching JobCard.js:56 so the two views can never disagree about it.
 *
 * Returns `{ primary: null }` for a job with neither, and the row renders a
 * neutral placeholder so every row's text still starts at the same x position.
 */
export function jobDuration(job) {
  const est = Number(job?.estimated_time) || 0;
  const logged = Number(job?.total_time) || 0;
  return {
    primary: compactDuration(est) || compactDuration(logged),
    isEstimate: est > 0,
    logged: est > 0 ? compactDuration(logged) : null,
    overBudget: est > 0 && logged > est,
  };
}

/**
 * Which Schedule view opens by default.
 *
 * A phone opens on Agenda; anything wider opens on Board.
 *
 * ── Why this is split by device and not a single default ────────────────────
 *
 * The two audiences want different first screens, and until now both got
 * Board. A crew opens the app in a truck to find out what they are doing
 * today — Board answers "what state is everything in", which is a manager's
 * question, and it puts today's work behind a column choice. A manager on a
 * desktop opens it to move work between states, which is what Board is FOR
 * and where drag-to-change-status actually works (MouseSensor only).
 *
 * ── Why it is a function in a tested module ─────────────────────────────────
 *
 * Because it is a product decision, not a detail. Written inline it is an
 * unexplained ternary in a `useState` initialiser that the next person
 * "tidies". Here the reasoning survives, and the test pins both branches — a
 * regression that sent every phone back to Board would otherwise be invisible
 * to everyone who reviews on a laptop, which is everyone.
 *
 * Evaluated once, at mount. Rotating a tablet across the breakpoint must NOT
 * yank the view out from under someone mid-task, so callers pass this to a
 * `useState` initialiser and never re-apply it.
 */
export function defaultViewMode(isMobile) {
  return isMobile ? 'agenda' : 'board';
}

/**
 * Build the whole agenda.
 *
 * @param {object[]} jobs   already filtered by the board (type/worker filters)
 * @param {string} start    window start, 'YYYY-MM-DD' inclusive
 * @param {string} end      window end, 'YYYY-MM-DD' inclusive
 * @param {string} today    'YYYY-MM-DD', injected so this stays pure
 * @returns {{days: object[], unscheduled: object[], unassignedCount: number, isEmpty: boolean}}
 *
 * A day appears only if it has jobs — seven headings for two days of work is
 * the noise this view is supposed to remove — with ONE exception: today is
 * always shown when it falls inside the window, so "you have nothing on today"
 * is stated rather than left to be inferred from an absence.
 */
export function buildAgenda(jobs, start, end, today) {
  const list = Array.isArray(jobs) ? jobs.filter(Boolean) : [];
  const days = [];

  if (start && end && start <= end) {
    for (let iso = start; iso <= end; iso = addDaysISO(iso, 1)) {
      const dayJobs = list.filter((j) => jobOnDay(j, iso)).sort(agendaOrder);
      if (dayJobs.length === 0 && iso !== today) continue;
      days.push({ ...dayHeading(iso, today), jobs: dayJobs });
    }
  }

  const unscheduled = list.filter(isUnscheduled).sort(agendaOrder);

  // Counted across the days only. An unscheduled job has no crew AND no date;
  // calling that out as "needs crew" would be pointing at the wrong problem.
  //
  // De-duped by id, because a multi-day job appears under every day it spans —
  // counting the rows would report one three-day build as three missing crews.
  const unassignedIds = new Set();
  days.forEach((d) => d.jobs.forEach((j) => {
    if (j.status !== 'completed' && isUnassigned(j)) unassignedIds.add(j.id);
  }));

  return {
    days,
    unscheduled,
    unassignedCount: unassignedIds.size,
    isEmpty: days.every((d) => d.jobs.length === 0) && unscheduled.length === 0,
  };
}
